import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { environment } from '../../../environments/environment';

// Microsoft Clarity project id (previously injected globally in main.ts).
const CLARITY_PROJECT_ID = 'qpla2wiem3';

// Route prefixes that make up the Social Listening funnel Clarity is scoped to:
// the SL onboarding funnel plus the "first steps" of project creation (the
// per-entity setup wizards and the post-create analysis-loading screen).
// Entity/data pages such as
// `/social-listening/my-brand/:id` are deliberately absent, so a user stops
// being tracked once they move past the first steps into real SL data.
const SL_FUNNEL_PREFIXES: readonly string[] = [
	'/onboarding/social-listening',
	'/social-listening/analysis-loading',
	'/social-listening/brand-setup',
	'/social-listening/competitors-setup',
	'/social-listening/industry-setup'
];

// Loads Microsoft Clarity ONLY for the Social Listening funnel. Clarity has no
// reliable runtime pause, so we control the one thing that is reliable: whether
// the tag is injected at all. The tag is injected once, the first time the user
// enters the funnel, and never for a session that stays outside it. Once
// injected it keeps recording for the rest of that page load (accepted trade-off
// — a non-SL session is still never recorded).
@Injectable({ providedIn: 'root' })
export class ClarityFunnelService {
	private injected = false;
	private readonly destroyed$ = new Subject<void>();

	constructor(private readonly router: Router) {}

	// Wired via APP_INITIALIZER so the router subscription is live before the
	// first navigation. No-op outside production, matching the prior footprint.
	init(): void {
		if (!environment.Flags.production) {
			return;
		}
		// Cover a deep-link/refresh that already landed in the funnel.
		this.evaluate(this.router.url);
		this.router.events
			.pipe(
				filter(
					(event): event is NavigationEnd =>
						event instanceof NavigationEnd
				),
				takeUntil(this.destroyed$)
			)
			.subscribe(event => this.evaluate(event.urlAfterRedirects));
	}

	private evaluate(url: string): void {
		if (this.injected || !this.isInFunnel(url)) {
			return;
		}
		this.injected = true;
		this.destroyed$.next();
		this.destroyed$.complete();
		this.injectClarity();
	}

	private isInFunnel(url: string): boolean {
		const path = url.split('?')[0].split('#')[0];
		return SL_FUNNEL_PREFIXES.some(
			prefix => path === prefix || path.startsWith(`${prefix}/`)
		);
	}

	private injectClarity(): void {
		const script = document.createElement('script');
		script.type = 'text/javascript';
		script.text = `
			(function(c,l,a,r,i,t,y){c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);})(window, document, "clarity", "script", "${CLARITY_PROJECT_ID}");
		`;
		document.body.appendChild(script);
	}
}
